import {
  normalizarStatusCadastro,
  TOTAL_ETAPAS_PADRAO,
  obterTotalEtapasPorPrograma,
} from "../mappers/statusMapper.js";
import {
  cadastroRequerSupex,
  normalizarListaSolicitacoesExcecao,
} from "../mappers/supexMapper.js";
import { obterEtapaRejeicaoPorHint } from "../mappers/rejeicaoMapper.js";
import { obterTipoProgramaUsuarioLogado } from "../utils/tipoPrograma.js";

/**
 * Resolve o resumo de status (label, etapa, progresso, situação) a partir
 * de um item de acompanhamento. Replica o contrato da função
 * `obterResumoStatusItem` do CadastroEmpresa.
 *
 * Aceita `tipoPrograma` opcional — quando ausente, lê do JWT do usuário
 * logado. Isso permite que a lista (sem detalhe carregado) e o detalhe
 * usem o mesmo programa, refletindo fielmente a regra de etapas:
 *   - programa 0 (TCE) ou 2 (TCA Empregador): etapa extra "Aguardando
 *     Preenchimento do Aprendiz" entre "Cadastro inicial" e "Validação
 *     do preenchimento";
 *   - programa 1 (TCA): fluxo padrão sem etapa extra.
 */

export function obterResumoStatusItem(item, tipoPrograma) {
  // O item da listagem pode vir com `raw` (após selecionar/atualizar) ou
  // sem ele (na lista inicial). Para que tanto a lista quanto o detalhe
  // usem o mesmo pipeline, mesclamos item + raw e damos prioridade a `raw`
  // (mais completo), mantendo o próprio `item` como fonte do campo
  // `status` e dos campos top-level quando o detalhe ainda não foi carregado.
  const raw = item?.raw && Object.keys(item.raw || {}).length > 0 ? item.raw : item || {};
  const fonte = { ...(item || {}), ...raw };

  const tipoProgramaEfetivo = Number.isFinite(Number(tipoPrograma))
    ? Number(tipoPrograma)
    : obterTipoProgramaUsuarioLogado();

  const listaExcecoes = normalizarListaSolicitacoesExcecao(fonte, raw);
  const requerSupex = cadastroRequerSupex(fonte, raw, listaExcecoes, item);
  const etapaRejeicaoHint = obterEtapaRejeicaoPorHint(
    fonte,
    raw,
    item,
    requerSupex,
    tipoProgramaEfetivo,
  );

  const status = normalizarStatusCadastro(
    fonte,
    raw,
    requerSupex,
    etapaRejeicaoHint,
    tipoProgramaEfetivo,
  );

  if (!status) {
    return {
      statusLabel: "Sem status",
      statusCor: "#94a3b8",
      etapaAtual: 1,
      totalEtapas: obterTotalEtapasPorPrograma(tipoProgramaEfetivo),
      progresso: 0,
      situacao: null,
    };
  }

  const progresso = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        (Number(status.etapaAtual || 1) /
          Number(status.totalEtapas || TOTAL_ETAPAS_PADRAO)) *
          100,
      ),
    ),
  );

  // Apenas duas categorias finais são expostas:
  //   - "finalizadas": processo chegou ao fim (APROVADO / FINALIZADO)
  //   - "canceladas": processo reprovado, recusado ou rejeitado
  // O default `null` indica "em andamento" sem precisar do termo explícito.
  return {
    statusLabel: status.statusLabel,
    statusCor: status.statusCor,
    etapaAtual: status.etapaAtual,
    totalEtapas: status.totalEtapas,
    progresso,
    situacao: status.finalizado
      ? "finalizadas"
      : status.rejeitado
        ? "canceladas"
        : null,
  };
}

export function filtrarAcompanhamentosPorSituacao(
  lista,
  situacao = "todos",
  tipoPrograma,
) {
  if (!Array.isArray(lista)) return [];
  if (!situacao || situacao === "todos") return lista;

  return lista.filter(
    (item) => obterResumoStatusItem(item, tipoPrograma).situacao === situacao,
  );
}
